import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import AvailabilityStatus from '@bibliocommons/availability-status';
import { Link } from '@bibliocommons/base-links';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { bibShape } from '@bibliocommons/bc-prop-types';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import { NewWindow as IconNewWindow } from '@bibliocommons/base-icons';
import HoldCounts from 'app/components/shared/hold-counts';

import './AvailabilityBibBlock.scss';

const BIB_TYPE = {
  DIGITAL: 'DIGITAL',
  DIGITAL_RESOURCE: 'DIGITAL_RESOURCE',
  PHYSICAL: 'PHYSICAL'
};

const AvailabilityBibBlock = ({
  bib,
  fetchDigitalAvailability,
  hasAvailabilityStatus,
  hasFetchDigitalAvailabilityButton,
  hasHoldCounts,
  hasPhysicalAvailabilityLink,
  itemPosition,
  itemPositionGrouped,
  openAvailabilityOverlay,
  totalCount,
  trackViewAvailabilityClick,
  trackViewDigitalResourceClick
}) => {
  const intl = useIntl();
  const [isFetchingDigitalAvailability, setIsFetchingDigitalAvailability] = useState(false);

  const availabilityStatus = bib.get('availability');

  if (availabilityStatus.get('status') === 'RESOURCE_NOT_FOUND') {
    return (
      <AvailabilityStatus
        availabilityLocationType={availabilityStatus.get('availabilityLocationType')}
        rollUpStatus={availabilityStatus.get('status')}
        status={availabilityStatus.get('localisedStatus')}
      />
    );
  }

  const bibType = bib.getIn(['availability', 'bibType']);
  const format = bib.getIn(['briefInfo', 'format']);
  const metadataId = bib.getIn(['briefInfo', 'metadataId']);

  switch (bibType) {
    case BIB_TYPE.DIGITAL: {
      const hasFetched = availabilityStatus.get('status') !== 'UNKNOWN';
      const isFetching = !hasFetched && isFetchingDigitalAvailability;
      const isServiceAvailable = bib.getIn(['policy', 'materialPolicy', 'healthy']) ?? false;

      const handleFetchDigitalAvailabilityClick = () => {
        setIsFetchingDigitalAvailability(true);

        // The "bib" object on the list page does not have an `id`,
        // but does have a `briefInfo.id`.
        fetchDigitalAvailability(bib.get('id', bib.getIn(['briefInfo', 'id'])));
      };

      if (!isServiceAvailable) {
        return <FormattedMessage id="digital_service_unavailable" />;
      }

      if (hasFetchDigitalAvailabilityButton && !hasFetched) {
        return (
          <TextButton
            className="check-availability-button"
            handleClick={handleFetchDigitalAvailabilityClick}
            isLoading={isFetching}
            dataKey="check-availability-button"
          >
            <FormattedMessage id="check_digital_availability" />
          </TextButton>
        );
      }

      return (
        <>
          {hasAvailabilityStatus && (
            <AvailabilityStatus
              availabilityLocationType={availabilityStatus.get('availabilityLocationType')}
              rollUpStatus={availabilityStatus.get('statusType')}
              status={availabilityStatus.get('localisedStatus')}
            />
          )}
          {hasHoldCounts && (
            <div className="hold-counts">
              <HoldCounts availability={availabilityStatus} />
            </div>
          )}
        </>
      );
    }
    case BIB_TYPE.DIGITAL_RESOURCE: {
      const description = availabilityStatus.get('eresourceDescription');
      const url = availabilityStatus.get('eresourceUrl');
      const linkText = description || intl.formatMessage({ id: `full_download_${format.toLowerCase()}` });

      const handleClick = () => {
        trackViewDigitalResourceClick({ bib, eresourceDescription: description, eresourceUrl: url });
      };

      return (
        <a
          className="cp-availability-bib-block"
          href={url}
          onClick={handleClick}
          rel="noopener noreferrer"
          target="_blank"
        >
          <span aria-hidden="true">
            {linkText} <IconNewWindow className="icon-new-window" />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage id="ada_link_opens_new_window" values={{ linkText }} />
          </ScreenReaderMessage>
        </a>
      );
    }
    case BIB_TYPE.PHYSICAL: {
      const isHoldable = bib.getIn(['policy', 'holdable']);
      const location = availabilityStatus.get('availabilityLocationType');
      const linkText = intl.formatMessage({ id: 'Availability.detailsLink' });
      const status = hasAvailabilityStatus ? availabilityStatus.get('localisedStatus') : null;
      const title = bib.getIn(['briefInfo', 'title']);

      const handleLinkClick = () => {
        openAvailabilityOverlay(metadataId, itemPosition, itemPositionGrouped, totalCount);

        trackViewAvailabilityClick({ bib, linkText, itemPosition, itemPositionGrouped, totalCount });
      };

      return (
        <div className="cp-availability-bib-block">
          {hasAvailabilityStatus && (
            <AvailabilityStatus
              availabilityLocationType={location}
              rollUpStatus={availabilityStatus.get('statusType')}
              status={availabilityStatus.get('localisedStatus')}
            />
          )}
          {hasPhysicalAvailabilityLink && (
            <Link
              className={`availability-link ${hasAvailabilityStatus ? 'availability-link--shifted' : ''}`}
              dataKey="bib-availability-link"
              href={`/v2/availability/${metadataId}`}
              handleClick={handleLinkClick}
              preventDefault
            >
              <span aria-hidden="true" key="message">
                {linkText}
              </span>
              <ScreenReaderMessage key="sr-message">
                <FormattedMessage
                  id="Availability.ada_detailsLink"
                  values={{
                    title,
                    format: intl.formatMessage({
                      id: `FORMAT.${format.toLowerCase()}`
                    }),
                    status: status ? intl.formatMessage({ id: `circ_${status.toLowerCase()}` }, { location }) : ''
                  }}
                />
              </ScreenReaderMessage>
            </Link>
          )}
          {hasHoldCounts && isHoldable && (
            <div className="hold-counts">
              <HoldCounts availability={availabilityStatus} />
            </div>
          )}
        </div>
      );
    }
    default: {
      throw new Error(`Unknown bibType: ${bibType}`);
    }
  }
};

AvailabilityBibBlock.propTypes = {
  bib: bibShape.isRequired,
  fetchDigitalAvailability: PropTypes.func.isRequired,
  hasAvailabilityStatus: PropTypes.bool.isRequired,
  hasFetchDigitalAvailabilityButton: PropTypes.bool.isRequired,
  hasHoldCounts: PropTypes.bool.isRequired,
  hasPhysicalAvailabilityLink: PropTypes.bool.isRequired,
  openAvailabilityOverlay: PropTypes.func.isRequired,
  trackViewAvailabilityClick: PropTypes.func.isRequired,
  trackViewDigitalResourceClick: PropTypes.func.isRequired
};

export default AvailabilityBibBlock;
